import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarClock } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ChangePlanModal } from "@/pages/settings/ChangePlanModal";
import { tenantPlanPrice } from "@/lib/tenant-plan-pricing";
import type {
  PartnerBenefitStatus,
  TenantCurrentResponse,
  TenantPlanOption,
} from "@/services/authService";

export interface SettingsPlanCardProps {
  tenant: TenantCurrentResponse;
  plans: TenantPlanOption[];
  /** Null when the tenant has no active partner redemption to explain. */
  partnerBenefit: PartnerBenefitStatus | null;
  selectedPlanId: string | null;
  selectedBillingInterval: "monthly" | "annual";
  onPlanChange: (id: string) => void;
  onBillingIntervalChange: (v: "monthly" | "annual") => void;
  onUpdatePlan: () => void | Promise<void>;
  saving: boolean;
  onOpenLiteralEInfo: () => void;
}

function formatPeriodEnd(iso: string | null): string | null {
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split("-");
  if (!y || !m || !d) return null;
  return `${d}/${m}/${y}`;
}

export function SettingsPlanCard({
  tenant,
  plans,
  partnerBenefit,
  selectedPlanId,
  selectedBillingInterval,
  onPlanChange,
  onBillingIntervalChange,
  onUpdatePlan,
  saving,
  onOpenLiteralEInfo,
}: SettingsPlanCardProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const hasPaymentIssue =
    tenant.subscriptionState === "past_due" ||
    tenant.subscriptionState === "lapsed";

  // Prefer the server-resolved current plan (handles legacy/grandfathered ids
  // like `pro` that aren't in the selectable tiers); fall back to the tier list.
  const currentPlan =
    tenant.plan ?? plans.find((p) => p.id === tenant.planId) ?? null;
  const isAnnual = tenant.billingInterval === "annual";
  // What this tenant actually pays. For an Art. 5 (Literal E / IVA mínimo)
  // tenant the 80 UI credit is already deducted server-side, so a Tala
  // subscriber legitimately reads $0 here even though Tala is a billable tier.
  const amount = currentPlan
    ? tenantPlanPrice(currentPlan, isAnnual ? "annual" : "monthly")
    : 0;
  const isFree = !currentPlan || amount === 0;
  const periodLabel = isAnnual
    ? t("settings.plans.perYear")
    : t("settings.plans.perMonth");
  const periodEnd = formatPeriodEnd(tenant.planSubscriptionPeriodEnd);
  const planName = currentPlan ? t(currentPlan.nameKey) : "—";

  const handleUpdateAndClose = async (): Promise<void> => {
    await onUpdatePlan();
    setOpen(false);
  };

  return (
    <>
      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground">
            {t("settings.plans.title")}
          </CardTitle>
          <CardDescription>{t("settings.plans.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {hasPaymentIssue ? (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              {tenant.subscriptionState === "lapsed"
                ? t(
                    "settings.plans.lapsedWarning",
                    "Tu plan venció porque no pudimos cobrar la suscripción. Actualizá tu forma de pago para reactivarlo.",
                  )
                : t(
                    "settings.plans.pastDueWarning",
                    "No pudimos cobrar tu suscripción. Actualizá tu forma de pago para no perder el plan.",
                  )}
            </div>
          ) : null}
          <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/30 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 space-y-1">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t("settings.plans.currentPlanLabel", "Tu plan actual")}
              </p>
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
                <span className="text-xl font-semibold text-foreground">
                  {planName}
                </span>
                {isFree ? (
                  <span className="text-sm text-muted-foreground">
                    {t("settings.plans.freeLabel")}
                  </span>
                ) : (
                  <>
                    <span className="text-sm font-medium text-foreground">
                      ${amount.toLocaleString("es-UY")} UYU
                    </span>
                    <span className="text-xs text-muted-foreground">
                      / {periodLabel}
                    </span>
                    <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/80">
                      {t("settings.plans.plusIva", "+ IVA")}
                    </span>
                  </>
                )}
              </div>
              {!isFree && periodEnd ? (
                <p
                  className={`flex items-center gap-1.5 text-xs ${
                    hasPaymentIssue
                      ? "font-medium text-destructive"
                      : "text-muted-foreground"
                  }`}
                >
                  <CalendarClock className="h-3.5 w-3.5" aria-hidden />
                  {hasPaymentIssue
                    ? t("settings.plans.endedOnLabel", "Venció el {{date}}", {
                        date: periodEnd,
                      })
                    : t("settings.plans.renewsOnLabel", "Renueva el {{date}}", {
                        date: periodEnd,
                      })}
                </p>
              ) : null}
              {/* dLocal Go has no in-place card change — its own help centre
                  says to cancel and re-subscribe. Say so here rather than let
                  the button imply an edit that cannot happen. */}
              {hasPaymentIssue ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  {t(
                    "settings.plans.reactivateHint",
                    "Vas a suscribirte otra vez: dLocal no permite cambiar la tarjeta de una suscripción existente.",
                  )}
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              {hasPaymentIssue ? (
                <Button type="button" onClick={() => setOpen(true)}>
                  {t("settings.plans.reactivateButton", "Reactivar suscripción")}
                </Button>
              ) : null}
              <Button
                type="button"
                onClick={() => setOpen(true)}
                variant="outline"
              >
                {t("settings.plans.changeButton", "Cambiar plan")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <ChangePlanModal
        open={open}
        onOpenChange={setOpen}
        tenant={tenant}
        plans={plans}
        partnerBenefit={partnerBenefit}
        selectedPlanId={selectedPlanId}
        selectedBillingInterval={selectedBillingInterval}
        onPlanChange={onPlanChange}
        onBillingIntervalChange={onBillingIntervalChange}
        onUpdatePlan={handleUpdateAndClose}
        saving={saving}
        onOpenLiteralEInfo={onOpenLiteralEInfo}
      />
    </>
  );
}
